import React from 'react';
import { Button, Form, FormControl, InputGroup, OverlayTrigger } from 'react-bootstrap';
import ReactTooltip from 'react-tooltip';
import { ArrowUpward } from '@material-ui/icons';
import { Filter, Search } from 'react-feather';
import styled from 'styled-components';
import mediaQuery from '../../util/media-query';
import LoadingButton from '../LoadingButton';
import { IAuthContext } from '../../context/auth/auth-context';
import withAuthContext from '../../context/auth/AuthConsumer';
import { TIERS } from '../../types/tiers';
import { useSelector } from 'react-redux';
import { ISearch } from '../../store/aiearth/types';
import EnviroAiTriangleIcon from '../../assets/enviro.svg';
import ReactGA from 'react-ga4';
import { CommonPromptBox } from '../ui';
import JurisdictionChip from '../JurisdictionChip';

const SearchFormContainer = styled(Form)<{ issearch: string }>`
  max-width: ${(props) => (props.issearch ? '900px' : '770px')};
  padding: 0;
  width: 100%;
  display: flex;
  justify-content: ${(props) => (props.issearch ? 'space-between' : 'center')};
  align-items: center;
  margin: ${(props) => (props.issearch ? '-20px 0 0 84px' : 'auto')};
  @media only screen and (max-width: 1000px) {
    margin: ${(props) => (props.issearch ? '15px 0 0' : 'auto')};
  }
  &:not(.cmn-searchform--hero) .btn-primary {
    background-color: #fff;
    border: 0;
    ${(props) =>
      props.issearch ? 'border-bottom-right-radius: 40px !important;' : 'border-bottom-left-radius: 40px !important;'};
    ${(props) =>
      props.issearch ? 'border-top-right-radius: 40px !important;' : 'border-top-left-radius: 40px !important;'};

    padding: 5px 20px !important;
    width: 60px;
    & .text-light {
      color: #005fbc !important;
      width: 1.5rem;
      height: 1.5rem;
      border-width: 0.2em;
    }
    &:focus {
      box-shadow: 0 0 0 0 rgb(38 119 198 / 50%);
    }
    &:not(:disabled):not(.disabled):active {
      background-color: #fff;
      border-color: #fff;
    }
  }
  & .input-group {
    box-shadow: 0 0px 5px 1px rgb(0 0 0 / 50%);
    border-radius: 50px;
  }
  & .form-control {
    border: 0;
    ${(props) => (props.issearch ? 'border-bottom-left-radius: 40px;' : 'border-bottom-right-radius: 40px;')};
    ${(props) => (props.issearch ? 'border-top-left-radius: 40px;' : 'border-top-right-radius: 40px;')};
    padding-left: ${(props) => (props.issearch ? '20px' : '15px')};
    height: ${(props) => (props.issearch ? '44px' : '60px')};
    @media only screen and (max-width: 1300px) {
      height: 44px;
    }
    font-size: 16px;
    &:focus {
      box-shadow: 0 0 0 0 rgb(0 95 188 / 25%);
    }
  }

  /* Light autofill only for default search; hero uses dark transparent field (see common-ui.css). */
  &:not(.cmn-searchform--hero) .form-control {
    &:-internal-autofill-selected {
      background-color: #fff !important;
      -webkit-box-shadow: 0 0 0 50px white inset;
      -webkit-text-fill-color: #333;
    }
    &:-webkit-autofill {
      -webkit-box-shadow: 0 0 0 50px white inset;
      -webkit-text-fill-color: #333;
    }
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 50px white inset;
      -webkit-text-fill-color: #333;
    }
  }

  &.cmn-searchform--hero .form-control {
    background-color: transparent !important;
    &:-internal-autofill-selected,
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px rgba(10, 10, 12, 0.92) inset !important;
      box-shadow: 0 0 0 1000px rgba(10, 10, 12, 0.92) inset !important;
      -webkit-text-fill-color: #f4f4f5 !important;
      caret-color: #f4f4f5;
      transition: background-color 99999s ease-out 0s;
    }
  }
`;

const FilterButton = styled.div<{ isApplied: boolean }>`
  ${(props) =>
    props.isApplied &&
    `svg {
      fill: rgb(0, 95, 188);
  }`}
  cursor: pointer;
  color: var(--white);
  margin-left: 1em;
  ${mediaQuery.phone`margin-top: .1em; margin-left: 5px;`}
  ${mediaQuery.tablet`margin-top: .1em; margin-left: 5px;`}
`;

interface ISearchForm {
  onSearchSubmit: (queryText: string) => void;
  isLoading: boolean;
  inputRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  searchRef?: React.MutableRefObject<any>;
  onFilterClick: () => void;
  onEnviroFilterClick: () => void;
  isFilterApplied: boolean;
  queryText: string;
  setQueryText: (queryText: string) => void;
  variant?: 'default' | 'hero';
}

const googleSearch = (text: string) => {
  const arraSplit = text.split(' ');
  if (arraSplit[0] === '#g') {
    const searchText = text.replace('#g', '');
    window.location.href = `https://www.google.com/search${searchText ? `?q=${searchText}` : '/webhp'}`;
    return false;
  }
  return true;
};

const SearchForm: React.FC<ISearchForm & { context: IAuthContext }> = (props) => {
  const authContext = props.context;
  const store: any = useSelector<any>((state): any => state);
  const searchState: ISearch = store.search;
  const previousPage = store.globalFlags.previousPage;
  const isBackButton = previousPage && ((previousPage === 'myenviroaiToInternetResults' &&  searchState.type === 'ALL') || (previousPage === 'myenviroaiToAgency' && searchState.type === 'default'));
  const variant = props.variant || 'default';
  const queryText = props.queryText || '';

  const submitSearch = (text: string) => {
    if (props.isLoading || !googleSearch(text)) {
      return;
    }
    ReactGA.event({
      category: 'Search',
      action: 'User typed in the search form',
      label: text,
      value: 1,
    });
    props.onSearchSubmit(text);
  };

  const filterControls = (
    <>
      {authContext.isAuthenticated &&
        searchState.type === 'default' &&
        (authContext.isLubrizol() || authContext.isTier(TIERS.PLATINUM) || authContext.isTier(TIERS.DAYPASS) || authContext.isTier(TIERS.SUPER_ADMIN)) && (
          <OverlayTrigger overlay={<ReactTooltip place="bottom" effect="solid" />}>
            <FilterButton data-tip="Advanced Filter" isApplied={props.isFilterApplied} onClick={() => props.onFilterClick()}>
              <Filter color="#3aefb8" />
            </FilterButton>
          </OverlayTrigger>
        )}

      {authContext.isAuthenticated && isBackButton &&
        (authContext.isLubrizol() || authContext.isTier(TIERS.PLATINUM) || authContext.isTier(TIERS.DAYPASS) || authContext.isTier(TIERS.SUPER_ADMIN)) && (
          <OverlayTrigger overlay={<ReactTooltip place="bottom" effect="solid" />}>
            <Button data-tip="Enviro Filter" variant="link" onClick={() => props.onEnviroFilterClick()}>
              <img src={EnviroAiTriangleIcon} alt="Enviro Ai Logo" width="30" />
            </Button>
          </OverlayTrigger>
        )}
    </>
  );

  if (variant === 'hero') {
    return (
      <SearchFormContainer
        as="div"
        className="cmn-searchform cmn-searchform--hero searchbox searchbox--hero"
        issearch={searchState && searchState.isSearch ? 'yes' : ''}
      >
        <div style={{ width: 'min(680px, calc(100vw - 140px))', minWidth: 320, margin: '0 auto', flex: 1 }}>
          <CommonPromptBox
            value={queryText}
            onChange={(e) => props.setQueryText(e.target.value)}
            onSubmit={() => submitSearch(queryText)}
            className="cmn-chem-searchbox"
            variant="hero"
            placeholder="How can I help you?"
            inputRef={props.inputRef}
            disabled={props.isLoading}
            leftSlot={<JurisdictionChip />}
            submitIcon={<ArrowUpward className="cmn-chem-searchbox__send-icon" style={{ fontSize: 24 }} />}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitSearch(queryText);
              }
            }}
          />
        </div>
        {filterControls}
      </SearchFormContainer>
    );
  }

  return (
    <SearchFormContainer
      className="cmn-searchform searchbox"
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        const text = props.inputRef?.current?.value ?? queryText;
        submitSearch(text);
      }}
      issearch={searchState && searchState.isSearch ? 'yes' : ''}
    >
      <InputGroup>
        {!searchState.isSearch && (
          <InputGroup.Append>
            <LoadingButton searchBtn={props.searchRef} isLoading={props.isLoading || false} className="arrow-btn">
              <Search className="cmn-searchform__icon" color="#005fbc" size={32} />
            </LoadingButton>
          </InputGroup.Append>
        )}

        <FormControl
          ref={props.inputRef as React.MutableRefObject<HTMLInputElement | null>}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.setQueryText(e.target.value)}
          value={queryText}
          placeholder="How can I help you?"
          name="inputRef"
          aria-label="Search"
          aria-describedby="basic-addon2"
          disabled={props.isLoading || false}
        />
        {searchState.isSearch && (
          <InputGroup.Append>
            <LoadingButton
              searchBtn={props.searchRef}
              isLoading={props.isLoading || false}
              className="arrow-btn"
            >
              <Search className="cmn-searchform__icon" color="#005fbc" size={20} />
            </LoadingButton>
          </InputGroup.Append>
        )}
      </InputGroup>

      {filterControls}
    </SearchFormContainer>
  );
};

export default withAuthContext(SearchForm);
